import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Focus model
 * -----------
 * `focusY(scrollTop)` is the absolute page-y at which "focus" sits — the
 * indicator's anchor, the play-from-here target, the next-chunk decision
 * input for the audio player.
 *
 * Linear interpolation from the first chunk's centre (C_0) to the last
 * chunk's centre (C_n) as scrollTop goes from 0 to maxScroll:
 *
 *   focusY = C_0 + (scrollTop / maxScroll) · (C_n − C_0)
 *
 * Constant slope means no acceleration at the edges — focus drifts
 * smoothly from near the top of the viewport to near the bottom as
 * the user scrolls, passing through the viewport centre at the article
 * midpoint. The inverse is well-defined, so `scrollToChunk` can target
 * any chunk without clamping.
 */

interface FocusGeometry {
  maxScroll: number;
  C0: number;
  Cn: number;
}

function chunkCenter(el: HTMLElement, container: HTMLElement): number {
  const rect = el.getBoundingClientRect();
  const containerTop = container.getBoundingClientRect().top;
  return rect.top - containerTop + container.scrollTop + el.offsetHeight / 2;
}

function geometryFor(
  container: HTMLElement,
  chunks: HTMLDivElement[],
): FocusGeometry | null {
  if (chunks.length === 0) return null;
  const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
  const C0 = chunkCenter(chunks[0], container);
  const Cn = chunkCenter(chunks[chunks.length - 1], container);
  return { maxScroll, C0, Cn };
}

function focusYAt(scrollTop: number, g: FocusGeometry): number {
  if (g.maxScroll <= 0) return g.C0;
  const t = scrollTop / g.maxScroll;
  return g.C0 + t * (g.Cn - g.C0);
}

function scrollTopForFocusY(target: number, g: FocusGeometry): number {
  if (g.maxScroll <= 0) return 0;
  const span = g.Cn - g.C0;
  if (span <= 0) return 0;
  const t = (target - g.C0) / span;
  return Math.max(0, Math.min(t * g.maxScroll, g.maxScroll));
}

export function useVisibleChunk() {
  const chunkRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());
  const [focusedChunkIndex, setFocusedChunkIndex] = useState(0);

  const setChunkRef = useCallback((index: number, el: HTMLDivElement | null) => {
    if (el) {
      chunkRefsMap.current.set(index, el);
    } else {
      chunkRefsMap.current.delete(index);
    }
  }, []);

  const orderedChunks = useCallback((): { index: number; el: HTMLDivElement }[] => {
    return [...chunkRefsMap.current.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([index, el]) => ({ index, el }));
  }, []);

  const getFocusedChunkIndex = useCallback((): number => {
    const container = document.querySelector('main');
    if (!(container instanceof HTMLElement)) return 0;

    const chunks = orderedChunks();
    if (chunks.length === 0) return 0;

    const geometry = geometryFor(container, chunks.map((c) => c.el));
    if (!geometry) return 0;

    const focusY = focusYAt(container.scrollTop, geometry);

    let bestIndex = chunks[0].index;
    let bestDistance = Infinity;
    for (const { index, el } of chunks) {
      const top = chunkCenter(el, container) - el.offsetHeight / 2;
      const bottom = top + el.offsetHeight;
      if (top <= focusY && focusY <= bottom) return index;
      const centre = (top + bottom) / 2;
      const distance = Math.abs(centre - focusY);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    }
    return bestIndex;
  }, [orderedChunks]);

  const scrollToChunk = useCallback((index: number) => {
    const el = chunkRefsMap.current.get(index);
    const container = document.querySelector('main');
    if (!el || !(container instanceof HTMLElement)) return;

    // Focus is a containment relation: a chunk is focused over the range
    // of scrollTops for which focusY lands inside its bbox. Snapping to
    // the exact-centre solution would shift scroll by up to half a chunk
    // height even when the user can already see the chunk — visible as a
    // small jump on audio start (playFromHere passes the focused chunk)
    // and on chunk transitions where the user scrolled to a different
    // chunk during playback. Auto-advance to chunk+1 still scrolls,
    // because the just-finished chunk is the focused one, not the next.
    if (getFocusedChunkIndex() === index) return;

    const chunks = orderedChunks();
    const geometry = geometryFor(container, chunks.map((c) => c.el));
    if (!geometry) return;

    const targetFocusY = chunkCenter(el, container);
    const targetTop = scrollTopForFocusY(targetFocusY, geometry);

    const startTop = container.scrollTop;
    const distance = targetTop - startTop;
    if (Math.abs(distance) < 1) return;

    const duration = Math.min(800, Math.abs(distance) * 1.5);
    const startTime = performance.now();
    const ease = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      container.scrollTop = startTop + distance * ease(progress);
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [orderedChunks, getFocusedChunkIndex]);

  // Track the focused chunk reactively so the UI can render an indicator
  // bar on whichever chunk play-from-here would target. Seed once after
  // mount, then update on every scroll.
  useEffect(() => {
    const container = document.querySelector('main');
    if (!container) return;

    setFocusedChunkIndex(getFocusedChunkIndex());

    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setFocusedChunkIndex(getFocusedChunkIndex());
      });
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [getFocusedChunkIndex]);

  return {
    getFocusedChunkIndex,
    focusedChunkIndex,
    setChunkRef,
    scrollToChunk,
  };
}
